import {
  EllipsisHorizontalIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Typography from "@tiptap/extension-typography";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import clsx from "clsx";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetInstance } from "src/api/General/instance";
import {
  NoteData,
  useCreateCompanyNote,
  useDeleteNote,
  useGetCompanyNotes,
  useUpdateNote,
} from "src/api/Notes/notes";
import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import { Loading } from "src/components/ui/loading";
import { TextInput } from "src/components/ui/text-input";
import { MenuBar } from "../Services/Notes/PhaseNotes";

type Props = {};

export default function CompanyNotes({}: Props) {
  const { companyId } = useParams();
  const { data: notes } = useGetCompanyNotes(String(companyId));
  const [search, setSearch] = useState("");
  const filteredNotes =
    notes
      ?.filter((n) => n.name.toLowerCase().includes(search.toLowerCase()))
      .sort(
        (n1, n2) =>
          new Date(n2.createdAt).getTime() - new Date(n1.createdAt).getTime()
      ) ?? [];
  return (
    <>
      {!!notes ? (
        <>
          {notes.length > 0 ? (
            <>
              <div className="flex h-16 items-center justify-between border-b px-8">
                <div className="relative">
                  <TextInput
                    className="h-8 w-72 pl-8"
                    placeholder="Search.."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-2.5">
                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
                <div>
                  <NewNoteButton />
                  {/* {resourcesCtx.state.isExternal ?  */}
                </div>
              </div>
              <div className="border-b text-sm">
                <div className="flex h-11 items-center justify-between gap-6 bg-gray-50 px-8 text-gray-500">
                  <div className="flex items-center space-x-8">
                    {/* <div className="w-10">Type</div> */}
                    <div className="min-w-[160px] flex-1">Name</div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-40">Created By</div>
                    <div className="w-32">Created At</div>
                  </div>
                </div>
              </div>
              <div className="overflow-auto">
                {filteredNotes.map((n) => (
                  <NoteRow key={n._id} note={n} />
                ))}
                {filteredNotes.length === 0 && (
                  <div className="flex h-12 items-center border-b border-gray-100 px-8 text-sm text-gray-400">
                    <div>No notes found..</div>
                  </div>
                )}
              </div>
            </>
          ) : (
            // <EmptySection
            //   Icon={DocumentTextIcon}
            //   subtitle="Resources enable easy access to documents and links. Add your first resource by clicking the button below."
            //   title="Resources"
            //   cta={<AddResourceButton />}
            //   />

            <div className="mx-auto mt-[10vh] w-fit">
              <div className="flex">
                <div className="h-20 w-20"></div>
                <div className="h-20 w-[420px] bg-gradient-to-t from-gray-200 to-transparent px-[1px]">
                  <div className="h-full w-full bg-white" />
                </div>
                <div className="h-20 w-20"></div>
              </div>
              <div className="flex">
                <div className="w-20 bg-gradient-to-l from-gray-200 to-white py-[1px]">
                  <div className="h-full w-full bg-white" />
                </div>
                <div className="w-[420px] border bg-gray-50/70 p-12">
                  <div className="text-lg font-medium">No Notes</div>
                  <div className="pt-4 font-light leading-relaxed text-gray-700">
                    Notes help you keep track of information related to the
                    company. They are hidden from external members. Begin by
                    creating a note.
                  </div>
                  <div className="flex space-x-2.5 pt-7">
                    <NewNoteButton />
                  </div>
                </div>
                <div className="w-20 bg-gradient-to-r from-gray-200 to-white py-[1px]">
                  <div className="h-full w-full bg-white" />
                </div>
              </div>
              <div className="flex">
                <div className="h-20 w-20"></div>
                <div className="h-20 w-[420px] bg-gradient-to-b from-gray-200 to-white px-[1px]">
                  <div className="h-full w-full bg-white" />
                </div>
                <div className="h-20 w-20"></div>
              </div>
            </div>
            // <div className="relative mx-auto mt-[15vh] flex w-96 flex-col items-center p-8">
            //   <DocumentTextIcon className="h-7 w-7 text-gray-400" />
            //   <div className="mt-1 text-xl font-medium text-gray-700">
            //     Notes
            //   </div>
            //   <div className="mb-2 mt-1 text-center font-light text-gray-500">
            //     Notes help you keep track of project updates. They are hidden
            //     from external members.
            //   </div>
            //   <div className="mt-3">
            //     <NewNoteButton />
            //   </div>
            // </div>
          )}
        </>
      ) : (
        <Loading className="mt-[20vh]" />
      )}
    </>
    // <div className="space-y-4">
    //   <div className="flex justify-end">

    //   </div>
    //   <div className="mx-auto max-w-5xl space-y-4 pb-6">
    //     {notes
    //       ?.sort(
    //         (n1, n2) =>
    //           new Date(n2.createdAt).getTime() -
    //           new Date(n1.createdAt).getTime()
    //       )
    //       .map((n) => (
    //         <NoteCard key={n._id} note={n} />
    //       ))}
    //   </div>
    // </div>
  );
}

function NewNoteButton() {
  const [isAddingNote, setIsAddingNote] = useState(false);
  return (
    <>
      <Button
        className="space-x-2"
        variant={"secondary"}
        onClick={() => {
          setIsAddingNote(true);
        }}
      >
        {/* <PlusIcon className="h-4 w-4" /> */}
        <span>New Note</span>
      </Button>
      <NewNoteModal
        isAddingNote={isAddingNote}
        setIsAddingNote={setIsAddingNote}
      />
    </>
  );
}

function NewNoteModal({
  isAddingNote,
  setIsAddingNote,
}: {
  isAddingNote: boolean;
  setIsAddingNote: (isAddingNote: boolean) => void;
}) {
  const { companyId } = useParams();
  const [name, setName] = useState("");
  const [isVisible, setIsVisible] = useState(true);
  // const editor = useEditor({
  //   extensions: [
  //     StarterKit,
  //     Typography,
  //     Placeholder.configure({
  //       placeholder: "Enter your note..",
  //       showOnlyWhenEditable: false,
  //     }),
  //     Link.configure({
  //       openOnClick: false,
  //       validate: (href) => /^https?:\/\//.test(href),
  //     }),
  //   ],
  //   editorProps: {
  //     attributes: {
  //       class:
  //         "prose outline-none ring-0 focus:outline-none focus:ring-0 border-none focus:border-none",
  //     },
  //   },
  //   content: "",
  //   editable: true,
  // });
  // const [content, setContent] = useState("");
  const createCompanyNoteMutation = useCreateCompanyNote();

  useEffect(() => {
    if (createCompanyNoteMutation.isSuccess) {
      // setContent("");
      setName("");
      setIsVisible(true);
      setIsAddingNote(false);
      editor?.commands.clearContent();
    }
  }, [createCompanyNoteMutation.isSuccess]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        autolink: true,
      }),
      Typography,
      Placeholder.configure({
        placeholder: "Enter your note..",
        showOnlyWhenEditable: false,
      }),
    ],
    content: "",
    // onBlur: ({ editor }) => {
    //   setContent(editor.getHTML());
    // },
  });
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  // useLayoutEffect(() => {
  //   if (textAreaRef.current) {
  //     textAreaRef.current.style.height = "0px"; // Reset height to shrink if needed
  //     const scrollHeight = textAreaRef.current.scrollHeight;
  //     textAreaRef.current.style.height = scrollHeight + "px";
  //   }
  // }, [name]);

  // const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = useCallback(() => {
    const textarea = textAreaRef.current;
    if (textarea) {
      textarea.style.height = "auto"; // Reset the height
      textarea.style.height = `${textarea.scrollHeight}px`; // Set height to scrollHeight
    }
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [name, adjustHeight]);

  useEffect(() => {
    setTimeout(() => {
      adjustHeight();
    }, 50);
  }, [isExpanded]);

  return (
    <Dialog
      open={isAddingNote}
      onOpenChange={(open) => {
        setIsAddingNote(open);
      }}
    >
      <DialogContent
        className={clsx(
          "flex w-full flex-col p-6",
          isExpanded
            ? "h-full max-w-4xl"
            : "max-h-[60vh] min-h-[500px] max-w-2xl"
        )}
        showCloseButton={false}
      >
        <div className="flex items-start justify-between">
          <textarea
            rows={1}
            ref={textAreaRef}
            className="w-full resize-none border-none p-0 pr-5 text-xl font-medium outline-none placeholder:text-gray-300 focus:border-none focus:outline-none focus:ring-0"
            value={name}
            onChange={(e) => {
              setName(e.target.value.replace(/[\r\n]/g, ""));
            }}
            placeholder="Title..."
          />
          <div className="flex items-start">
            <Button
              tabIndex={-1}
              variant={"ghost"}
              className="h-7 w-7"
              onClick={() => {
                setIsExpanded((prev) => !prev);
              }}
            >
              {isExpanded ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  className="h-3 w-3 shrink-0"
                >
                  <polyline points="4 14 10 14 10 20" />
                  <polyline points="20 10 14 10 14 4" />
                  <line x1="14" x2="21" y1="10" y2="3" />
                  <line x1="3" x2="10" y1="21" y2="14" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3 shrink-0"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <line x1="21" x2="14" y1="3" y2="10" />
                  <line x1="3" x2="10" y1="21" y2="14" />
                </svg>
              )}
            </Button>
            <Button
              tabIndex={-1}
              variant={"ghost"}
              className="h-7 w-7"
              onClick={() => {
                setIsAddingNote(false);
              }}
            >
              <XMarkIcon className="h-4 w-4 shrink-0" />
            </Button>
          </div>
        </div>
        {/* <DialogHeader>
          <DialogTitle>New Note</DialogTitle>
        </DialogHeader> */}
        <div
          onClick={() => {
            editor?.commands.focus();
          }}
          // ref={editorDivRef}
          className={clsx(
            "flex h-full flex-1 flex-col overflow-auto rounded-md border border-gray-200",
            isExpanded ? "" : ""
          )}
        >
          {editor && (
            <div className="border-b bg-gray-50 p-3 py-1.5">
              <MenuBar editor={editor} />
            </div>
          )}
          <div className="relative flex-1 overflow-auto p-3">
            <EditorContent
              editor={editor}
              className="prose h-full w-full max-w-none focus:border-none focus:outline-none focus:ring-0"
            />
          </div>
        </div>
        {/* <div
          className={clsx(
            "min-h-[400px] flex-1 cursor-text overflow-y-auto overflow-x-hidden rounded-md border border-gray-200 p-3 shadow-sm",
            isExpanded ? "h-full" : "max-h-[60vh]"
          )}
          onClick={() => {
            editor?.commands.focus();
          }}
        >
          {editor && <TiptapEditor editor={editor} />}
        </div> */}
        <DialogFooter>
          <div className="flex w-full justify-end">
            <div className="flex justify-end space-x-2.5">
              <Button
                disabled={
                  createCompanyNoteMutation.isLoading ||
                  name.trim() === "" ||
                  !editor
                }
                onClick={() => {
                  if (editor) {
                    createCompanyNoteMutation.mutate({
                      params: {
                        name,
                        description: "",
                        content: editor?.getHTML(),
                        isVisible,
                        customerId: String(companyId),
                      },
                    });
                  }
                }}
              >
                {createCompanyNoteMutation.isLoading ? "Creating..." : "Create"}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NoteRow({ note }: { note: NoteData }) {
  const updateNoteMutation = useUpdateNote();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const deleteNoteMutation = useDeleteNote();
  const [name, setName] = useState(note.name);
  const [isDeleting, setIsDeleting] = useState(false);
  const editorDivRef = useRef<HTMLDivElement>(null);
  const [optionsOpen, setOptionsOpen] = useState(false);

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = "0px"; // Reset height to shrink if needed
      const scrollHeight = textAreaRef.current.scrollHeight;
      textAreaRef.current.style.height = scrollHeight + "px";
    }
  }, [name]);

  useEffect(() => {
    setTimeout(() => {
      if (textAreaRef.current) {
        textAreaRef.current.style.height = "0px"; // Reset height to shrink if needed
        const scrollHeight = textAreaRef.current.scrollHeight;
        textAreaRef.current.style.height = scrollHeight + "px";
      }
      editor?.commands.focus("end");
      if (editorDivRef.current) {
        editorDivRef.current.scrollTop = editorDivRef.current.scrollHeight;
      }
    }, 50);
  }, [isEditing]);

  useEffect(() => {
    setName(note.name);
  }, [note.name]);

  const { instance } = useGetInstance();

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        autolink: true,
      }),
      Typography,
      Placeholder.configure({
        placeholder: "Enter your note..",
        showOnlyWhenEditable: false,
      }),
    ],
    content: note.content,
    onBlur: ({ editor }) => {
      if (note.content.trim() !== editor.getHTML().trim()) {
        updateNoteMutation.mutate({
          params: {
            _id: note._id.toString(),
            content: editor.getHTML(),
          },
          metadata: {
            taskId: note.task?._id,
            phaseId: note.phaseId,
          },
        });
      }
    },
    autofocus: true,
    editable: !note.readOnly,
  });

  useEffect(() => {
    setIsDeleting(false);
    setIsEditing(false);
  }, [deleteNoteMutation.isSuccess]);

  return (
    <>
      <div
        className="flex h-12 cursor-pointer items-center justify-between gap-6 border-b border-gray-100 px-8 hover:bg-gray-50"
        onClick={() => {
          setIsEditing(true);
        }}
      >
        <div className={clsx("flex min-w-0 flex-1 items-center space-x-8")}>
          {/* <div className="inline-flex w-10 items-center">
            {!!note.task ? (
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger>
                    <BoltIcon className="h-[18px] w-[18px] text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <div>Task</div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger>
                    <RocketLaunchIcon className="h-[18px] w-[18px] text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <div>Project</div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div> */}
          <div className="min-w-[160px] flex-1 truncate text-sm font-medium text-gray-600">
            {!!note.source ? (
              note.source === "hubspot" ? (
                <div className="flex items-center gap-1.5">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="3 3.5 12 12"
                    className="w-4"
                  >
                    {" "}
                    <path
                      fill="#FF5C35"
                      d="M11.54 7.422V6.096a1.02 1.02 0 0 0 .588-.92v-.03c0-.56-.46-1.02-1.02-1.02h-.03c-.561 0-1.02.46-1.02 1.02v.03c0 .407.243.757.589.92v1.326c-.516.08-.986.292-1.375.605l-3.64-2.832a1.14 1.14 0 0 0 .04-.286 1.15 1.15 0 1 0-1.15 1.148c.206 0 .398-.059.566-.154l3.58 2.786a2.892 2.892 0 0 0-.482 1.603c0 .62.196 1.194.527 1.666l-1.089 1.09a.935.935 0 0 0-.27-.045.945.945 0 1 0 .945.945.934.934 0 0 0-.044-.27l1.077-1.077a2.89 2.89 0 0 0 1.761.598 2.905 2.905 0 0 0 .446-5.776Zm-.447 4.36a1.49 1.49 0 1 1 0-2.98 1.49 1.49 0 0 1 0 2.98Z"
                    />
                  </svg>
                  <div>HubSpot Note</div>
                </div>
              ) : (
                "Salesforce Note"
              )
            ) : (
              note.name
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center space-x-4">
          <div className="w-40 truncate text-sm font-light text-gray-500">
            {!!note.source
              ? note.source === "hubspot"
                ? "HubSpot"
                : "Salesforce"
              : note.createdBy?.name}
          </div>
          <div className="w-32 text-sm font-light text-gray-500">
            {dayjs(note.createdAt).format("MMM DD, h:mm A")}
          </div>
        </div>
      </div>
      <Dialog
        open={isEditing}
        onOpenChange={(open) => {
          setIsEditing(open);
        }}
      >
        <DialogContent
          className={clsx(
            "flex w-full flex-col p-6",
            isExpanded
              ? "h-full max-w-4xl"
              : "max-h-[60vh] min-h-[500px] max-w-2xl"
          )}
          showCloseButton={false}
        >
          <div className="flex items-start justify-between">
            {note.source ? (
              <>
                {note.source === "hubspot" && (
                  <div className="flex items-center gap-1.5">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="3 3.5 12 12"
                      className="w-4"
                    >
                      {" "}
                      <path
                        fill="#FF5C35"
                        d="M11.54 7.422V6.096a1.02 1.02 0 0 0 .588-.92v-.03c0-.56-.46-1.02-1.02-1.02h-.03c-.561 0-1.02.46-1.02 1.02v.03c0 .407.243.757.589.92v1.326c-.516.08-.986.292-1.375.605l-3.64-2.832a1.14 1.14 0 0 0 .04-.286 1.15 1.15 0 1 0-1.15 1.148c.206 0 .398-.059.566-.154l3.58 2.786a2.892 2.892 0 0 0-.482 1.603c0 .62.196 1.194.527 1.666l-1.089 1.09a.935.935 0 0 0-.27-.045.945.945 0 1 0 .945.945.934.934 0 0 0-.044-.27l1.077-1.077a2.89 2.89 0 0 0 1.761.598 2.905 2.905 0 0 0 .446-5.776Zm-.447 4.36a1.49 1.49 0 1 1 0-2.98 1.49 1.49 0 0 1 0 2.98Z"
                      />
                    </svg>
                    <div>HubSpot Note</div>
                    {note.readOnly && <Badge variant={"gray"}>Read only</Badge>}
                  </div>
                )}
              </>
            ) : (
              <textarea
                rows={1}
                ref={textAreaRef}
                className="w-full resize-none border-none p-0 pr-5 text-xl font-medium outline-none placeholder:text-gray-300 focus:border-none focus:outline-none focus:ring-0"
                value={name}
                onChange={(e) => {
                  setName(e.target.value.replace(/[\r\n]/g, ""));
                }}
                onBlur={() => {
                  if (name.trim() !== note.name.trim() && name.trim() !== "") {
                    updateNoteMutation.mutate({
                      params: {
                        _id: note._id.toString(),
                        name,
                      },
                      metadata: {
                        taskId: note.task?._id,
                        phaseId: note.phaseId,
                      },
                    });
                  }
                }}
                placeholder="Title..."
              />
            )}
            <div className="flex items-start">
              <Button
                tabIndex={-1}
                variant={"ghost"}
                className="h-7 w-7"
                onClick={() => {
                  setIsExpanded((prev) => !prev);
                }}
              >
                {isExpanded ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    className="h-3 w-3 shrink-0"
                  >
                    <polyline points="4 14 10 14 10 20" />
                    <polyline points="20 10 14 10 14 4" />
                    <line x1="14" x2="21" y1="10" y2="3" />
                    <line x1="3" x2="10" y1="21" y2="14" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3 w-3 shrink-0"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <polyline points="15 3 21 3 21 9" />
                    <polyline points="9 21 3 21 3 15" />
                    <line x1="21" x2="14" y1="3" y2="10" />
                    <line x1="3" x2="10" y1="21" y2="14" />
                  </svg>
                )}
              </Button>
              {!note.readOnly && (
                <DropdownMenu
                  open={optionsOpen}
                  onOpenChange={(isOpen) => {
                    setOptionsOpen(isOpen);
                  }}
                >
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant={"ghost"}
                      className={clsx(
                        "h-7 w-7 p-0 hover:bg-gray-200/60 group-hover:visible",
                        !optionsOpen ? "bg-transparent" : "bg-gray-200/60"
                      )}
                    >
                      <EllipsisHorizontalIcon className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-44" align="end">
                    <DropdownMenuGroup>
                      <DropdownMenuItem
                        // className="py-1 focus:bg-gray-50 focus:text-red-600"
                        className="focus:bg-red-50 focus:text-red-600"
                        onClick={(e) => {
                          setIsDeleting(true);
                          e.stopPropagation();
                        }}
                      >
                        <TrashIcon className="h-3.5 w-3.5 shrink-0" />
                        <span className="ml-2 text-sm">Delete</span>
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Button
                tabIndex={-1}
                variant={"ghost"}
                className="h-7 w-7"
                onClick={() => {
                  setIsEditing(false);
                }}
              >
                <XMarkIcon className="h-4 w-4 shrink-0" />
              </Button>
            </div>
          </div>

          {/* <div className="space-y-1">
                <textarea
                  ref={textAreaRef}
                  className="w-full resize-none border-none p-0 pr-5 text-3xl font-medium outline-none placeholder:text-gray-300 focus:border-none focus:outline-none focus:ring-0"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                  }}
                  onBlur={() => {
                    if (name.trim() !== note.name.trim()) {
                      updateNoteMutation.mutate({
                        params: {
                          _id: note._id.toString(),
                          name,
                        },
                        metadata: {
                          taskId: note.task?._id,
                          phaseId: note.phaseId,
                        },
                      });
                    }
                  }}
                  placeholder="Title..."
                />
              </div> */}
          <div
            onClick={() => {
              editor?.commands.focus();
            }}
            ref={editorDivRef}
            className={clsx(
              "flex h-full flex-1 flex-col overflow-auto rounded-md border border-gray-200",
              isExpanded ? "" : "",
              note.readOnly ? "cursor-not-allowed" : ""
            )}
          >
            {editor && !note.readOnly && (
              <div className="border-b bg-gray-50 p-3 py-1.5">
                <MenuBar editor={editor} />
              </div>
            )}
            <div className="relative flex-1 overflow-auto p-3">
              <EditorContent
                editor={editor}
                className="prose h-full w-full max-w-none focus:border-none focus:outline-none focus:ring-0"
              />
            </div>
          </div>
          {/* <div
                className="max-h-[400px] min-h-[300px] cursor-text overflow-auto rounded-md border border-gray-300 p-3 shadow-sm"
                onClick={() => {
                  editor?.commands.focus();
                }}
                ref={editorDivRef}
              >
                <TiptapEditor editor={editor} />
              </div> */}
          {/* <div>
            <Button
              variant={"secondary"}
              size={"icon"}
              onClick={() => {
                setIsDeleting(true);
              }}
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          </div> */}
        </DialogContent>
      </Dialog>
      <Dialog
        open={isDeleting}
        onOpenChange={(open) => {
          setIsDeleting(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Confirmation</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-3 text-sm text-gray-600">
            {note.source === "hubspot" ? (
              <div>
                Are you sure you want to delete this note?{" "}
                <span className="font-medium text-black">
                  It will delete the note on HubSpot too.
                </span>
              </div>
            ) : (
              <div>
                Are you sure you want to delete the note{" "}
                <span className="font-medium text-gray-900">{note.name}</span>?
              </div>
            )}
          </div>
          <DialogFooter>
            <div className="flex justify-end space-x-3">
              <Button
                variant={"secondary"}
                onClick={() => {
                  setIsDeleting(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant={"destructive"}
                disabled={deleteNoteMutation.isLoading}
                onClick={() => {
                  deleteNoteMutation.mutate({
                    note: {
                      _id: note._id.toString(),
                    },
                    metadata: {
                      taskId: note.task?._id,
                      phaseId: note.phaseId,
                      // customerId: note.customerId,
                    },
                  });
                }}
              >
                {deleteNoteMutation.isLoading ? "Deleting.." : "Delete"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
